import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Shield, TrendingUp, Zap, BarChart3 } from "lucide-react";

const missionPillars = [
  { icon: TrendingUp, label: "Reducing Costs" },
  { icon: Shield, label: "Mitigating Risks" },
  { icon: Zap, label: "Enabling Innovation" },
];

const approachItems = [
  {
    icon: BarChart3,
    title: "Financial Efficiency",
    desc: "Optimize IT spend, reduce total cost of ownership (TCO), and ensure sustainable cost savings without compromising growth.",
  },
  {
    icon: Shield,
    title: "Risk & Compliance",
    desc: "Safeguard enterprise data, ensure audit readiness, and maintain compliance with global and local regulations.",
  },
  {
    icon: Zap,
    title: "Operational Resilience",
    desc: "Build scalable, secure, future-ready infrastructures that support business continuity and growth.",
  },
  {
    icon: TrendingUp,
    title: "Growth Enablement",
    desc: "Free leadership to focus on expansion and innovation by handling the technology complexities.",
  },
];

const impactStats = [
  { value: "100+", title: "ERP Implementations", desc: "With measurable ROI across industries" },
  { value: "30%", title: "Cost Savings", desc: "Achieved on IT operations for clients" },
  { value: "25+", title: "Enterprise Clients Impacted", desc: "Aligned with boardroom priorities" },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
              About TEQXL
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
              Your CIO as a Service Partner
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              TEQXL is the brainchild of like-minded, highly experienced IT professionals
              with a track record of delivering complex and advanced technology solutions for
              national and international organizations. We partner with business leaders to
              ensure technology delivers measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* About Image + Text */}
      <section className="py-16 lg:py-24 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="overflow-hidden">
              <img
                src="/digital-transformation-roadmap.png"
                alt="Digital Transformation Roadmap"
                className="w-full h-auto lg:h-[400px] lg:object-cover"
              />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
                Who We Are
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Deep global experience in enterprise-scale digital transformation
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed mb-6">
                We align IT investments with financial performance, operational resilience,
                and long-term growth. Our team enables clients to streamline operations,
                manage systems with precision, control, and confidence.
              </p>
              <p className="text-base text-[#4B5563] leading-relaxed">
                As your strategic technology partner, we ensure every IT decision strengthens
                your bottom line, enhances resilience, and accelerates business growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="mission-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-[#002FA7]" />
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7]">
                Our Mission
              </p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
              Unlock the full potential of technology
            </h2>
            <p className="text-base text-[#4B5563] leading-relaxed">
              We help leadership teams unlock the full potential of technology to drive
              sustainable shareholder value — by reducing costs, mitigating risks, and
              enabling innovation.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-gray-200">
            {missionPillars.map((item, i) => (
              <div
                key={item.label}
                className={`flex items-center gap-4 p-8 lg:p-10 ${
                  i < 2 ? "border-r border-gray-200" : ""
                }`}
              >
                <item.icon className="w-6 h-6 text-[#002FA7] shrink-0" />
                <span className="text-base font-semibold text-[#0A0A0A]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section data-testid="approach-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <div className="inline-flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-[#002FA7]" />
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7]">
                Our Approach
              </p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Business impact, not just systems
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-gray-200 bg-white">
            {approachItems.map((item, i) => (
              <div
                key={item.title}
                data-testid={`approach-item-${i}`}
                className={`p-8 lg:p-12 ${i % 2 === 0 ? "sm:border-r border-gray-200" : ""} ${
                  i < 2 ? "border-b border-gray-200" : ""
                }`}
              >
                <item.icon className="w-6 h-6 text-[#002FA7] mb-5" />
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section data-testid="impact-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Our Impact Delivered
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Measurable results across industries
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-gray-200">
            {impactStats.map((stat, i) => (
              <div
                key={stat.title}
                data-testid={`impact-stat-${i}`}
                className={`p-8 lg:p-12 ${i < 2 ? "sm:border-r border-gray-200" : ""}`}
              >
                <p className="font-heading text-4xl lg:text-5xl tracking-tighter font-bold text-[#002FA7] mb-3">
                  {stat.value}
                </p>
                <p className="text-base font-semibold text-[#0A0A0A] mb-2">{stat.title}</p>
                <p className="text-sm text-[#4B5563]">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise */}
      <section data-testid="promise-section" className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 mb-4">
            Our Promise
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6 max-w-2xl mx-auto">
            TEQXL is more than a consultancy
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            We are your strategic technology partner. Every IT decision we guide
            strengthens your bottom line, enhances resilience, and accelerates your
            business growth.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Partner With Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
